import type { OnboardingState, OnboardingStep } from '../components/Auth/AccountSetupBanner.types';

/** KYC statuses that count as "identity verified" (terminal / no further action needed).
 *  NOTE: `active` is deliberately excluded — KycStatusBadge renders it as "In Progress", so
 *  treating it as verified would hide the KYC section and show a "Verified" row while the badge
 *  still says in-progress (and would dead-end the KycGate "Continue Verification" CTA). */
const KYC_VERIFIED_STATUSES = ['verified', 'manually_reviewed', 'not_required_current_access'];

/** True when the user's identity verification is complete. Shared by the onboarding banner and
 *  the Settings registry (which hides the KYC section once verified). Demo flag counts too. */
export function isIdentityVerified(user: any): boolean {
  return KYC_VERIFIED_STATUSES.includes(user?.kyc_status || 'not_started');
}

// Build the AccountSetupBanner's onboarding state directly from the authenticated
// user's status fields. This backend has no dedicated onboarding-status endpoint, so
// we derive the four investor-onboarding steps from fields already on the user object
// (the same logic the retired InvestorAccessProgress used). Keeping this as an adapter
// means the banner UI stays a pure presentation layer.
export function buildOnboardingFromUser(
  user: any,
  // DEMO: `meetingScheduled` / `fundingConnected` let the portal mark those steps done after a
  // (mocked) booking / bank link, since the real backend has no matching field yet.
  opts?: { meetingScheduled?: boolean; fundingConnected?: boolean; identityVerified?: boolean },
): OnboardingState {
  const manualStatus = user?.manual_review_status || 'pending_review';
  const documentStatus = user?.document_access_status || 'pending_review';
  const eligibilityStatus = user?.investment_eligibility_status || 'view_only';
  const kycStatus = user?.kyc_status || 'not_started';

  const profileReviewed = ['approved', 'legacy_manual_review_approved'].includes(manualStatus);
  const documentsUnlocked = documentStatus === 'approved';
  const meetingDone = profileReviewed || documentsUnlocked || !!opts?.meetingScheduled;
  const identityVerified = isIdentityVerified({ kyc_status: kycStatus }) || !!opts?.identityVerified;
  // A linked bank clears the funding step (and thus the Settings "Action needed" hint on the Bank
  // Accounts row). Demo flag counts too, mirroring identity above.
  const fundingReady = eligibilityStatus === 'eligible_to_invest' || user?.investment_execution_approved === true || !!opts?.fundingConnected;

  // Exactly four steps, in fixed order — unchanged from what the portal showed before.
  const defs = [
    { id: 'create-account', title: 'Create Account', description: 'Set up your investor profile', completed: !!user, isAccessible: true },
    { id: 'schedule-meeting', title: 'Schedule a Meeting', description: 'Book your intro call', completed: meetingDone, isAccessible: true },
    // Identity is NOT gated on the meeting: the intro call is mandatory to finish onboarding
    // but is not a prerequisite for KYC (a user can verify identity first). Funding still
    // follows identity below.
    { id: 'verify-identity', title: 'Verify Identity', description: 'Complete identity verification', completed: identityVerified, isAccessible: true },
    { id: 'connect-funding', title: 'Connect Funding Method', description: 'Link how you’ll invest', completed: fundingReady, isAccessible: identityVerified },
  ];

  const steps: OnboardingStep[] = defs.map((d, i) => ({ ...d, order: i }));
  const completedSteps = steps.filter((s) => s.completed).map((s) => s.id);
  const completedCount = completedSteps.length;
  const nextStep = steps.find((s) => !s.completed && s.isAccessible) || steps.find((s) => !s.completed) || null;

  return {
    // Stay mounted at 100% too, so the banner can show its success state (the banner handles
    // dismissal). Only truly empty (no steps) hides it.
    shouldShowOnboardingBanner: steps.length > 0,
    completedSteps,
    // Pass full metadata for ALL steps here (not just incomplete ones) so the banner
    // renders them in fixed order rather than synthesising order-less stubs.
    remainingSteps: steps,
    nextStep,
    totalSteps: steps.length,
    completedCount,
    progressPercentage: Math.round((completedCount / steps.length) * 100),
  };
}
